import bzxApi, {
  getTreasuryBalances,
  IAssetsStatsHistory,
  IChainStat,
  IChainTokenStat,
} from 'app-lib/apis/bzxApi'
import { Asset } from 'app-lib/assetUtils'
import errorUtils from 'app-lib/errorUtils'
import protocolStatsUtils from 'app-lib/protocolStatsUtils'
import { chainConfigs, ChainId, supportedChains } from 'bzx-common/src/config/appConfig'
import ReserveDetails from 'bzx-common/src/domain/fulcrum/ReserveDetails'
import AsyncData from 'bzx-common/src/lib/mobx/AsyncData'
import { debounce, DebouncedFunc } from 'lodash'
import * as mobx from 'mobx'
import { BigNumber } from '@0x/utils'
import RootStore from '../RootStore'
import { Transaction } from '../TransactionStore'
import { IActivityInfo } from 'bzx-common/src/domain/txTypes'
import collectionUtils from 'bzx-common/src/lib/collectionUtils'

export interface TreasuryBalance {
  asset: Asset
  amount: BigNumber
  amountUSD: BigNumber
}

export interface IStatsStoreProps {
  errorStatsInfo: Error | null
  tokenRowsData: ReserveDetails[]
  totalsRowData: ReserveDetails | null
  treasuryBalances: AsyncData<TreasuryBalance[]>
  storedEvents: boolean
}

export interface ILatestActivity {
  chainId: ChainId
  txHash: string
  action: string
  value: BigNumber
  loanToken: Asset
  collateralToken: Asset
  leverage?: number
}

export default class StatsStore implements IStatsStoreProps {
  errorStatsInfo: Error | null = null
  rootStore: RootStore
  storedEvents = false
  tokenRowsData: ReserveDetails[] = []
  totalsRowData: ReserveDetails | null = null
  transactions: Transaction[] = []
  refreshLatestActivityDebounced: DebouncedFunc<() => Promise<IActivityInfo[]>>
  defaultQuantityEvents = 20
  periodChart = 1

  readonly treasuryBalances = new AsyncData([], getTreasuryBalances, { debounce: 300000 })

  /** Can fetch and update latest events per chain*/
  readonly latestProtocolEvents = new AsyncData<IActivityInfo[]>(
    [],
    async (chainId: ChainId) => {
      const latestProtocolEvents = [...this.latestProtocolEvents.value] as IActivityInfo[]
      const result = !latestProtocolEvents.find((event) => chainId === event.chainId)
        ? await protocolStatsUtils.getLatestProtocolEvents(chainId)
        : []

      const newEvents = result
        .concat(latestProtocolEvents)
        .sort((a, b) => Number(b.timestamp) - Number(a.timestamp))

      // newEvents.reduce()

      return newEvents
    },
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not get or update latest activity' })
        console.error(err)
      },
    }
  )

  /** Can fetch and update latest transactions for curret user*/
  readonly latestUserTransactions = new AsyncData<IActivityInfo[]>(
    [],
    async () => {
      const { currentAccount } = this.rootStore.fulcrumProvider
      return protocolStatsUtils.getUserProtocolEvents(currentAccount)
    },
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not get or update latest transactions' })
        console.error(err)
      },
    }
  )

  /** Can fetch events for selected user*/
  readonly selectedUserEvents = new AsyncData<IActivityInfo[]>(
    [],
    async (selectedUser) => {
      return protocolStatsUtils.getUserProtocolEvents(selectedUser)
    },
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not get events for selected user' })
        console.error(err)
      },
    }
  )

  /** Can fetch and update stats for all chains or a single chain */
  readonly stats: AsyncData<IChainStat[]> = new AsyncData<IChainStat[]>(
    [],
    async (chainId?: ChainId, opts?: { onChain: boolean }) => {
      const onChain = opts?.onChain || this.rootStore.settings.fetchRatesOnChain
      const fp = this.rootStore.fulcrumProvider
      // We make sure that only supported chain by the UI will be kept from the (api) response
      const chainStats = (await protocolStatsUtils.getChainStats(fp, chainId, { onChain })).filter(
        (stats) => supportedChains.includes(stats.chainId)
      )

      if (!chainId) {
        return chainStats
      }
      // if we fetched a single chain, we simply replace its data
      let newStats = [...this.stats.value]
      if (chainStats.length > 0) {
        newStats = newStats.filter((stat) => stat.chainId !== chainId).concat(chainStats)
      }
      return newStats
    },
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not fetch stats info' })
        console.error(err)
      },
    }
  )

  readonly tvlData: AsyncData<IAssetsStatsHistory[]> = new AsyncData<IAssetsStatsHistory[]>(
    [],
    async (chainId?: ChainId) => {
      if (!chainId) {
        return []
      }
      const tvl = await bzxApi.getAssetStatsHistory(chainId)
      if (!tvl) {
        return []
      }
      return tvl
    },
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not get or update tvl data' })
        console.error(err)
      },
    }
  )

  readonly assetStatsData: AsyncData<{ [key: string]: IAssetsStatsHistory[] }> = new AsyncData<{
    [key: string]: IAssetsStatsHistory[]
  }>(
    {},
    async (chainId?: ChainId) => {
      if (!chainId) {
        return {}
      }
      const history = await bzxApi.getAssetStatsHistory(chainId, true)
      if (!history) {
        return {}
      }
      // Group array of AssetsStatsHistory by token
      return collectionUtils.groupBy<IAssetsStatsHistory>(history, ['token'])
    },
    {
      onError(err) {
        errorUtils.decorateError(err, { title: 'Could not get or update asset stats data' })
        console.error(err)
      },
    }
  )

  /** How much the treasury is worth in USD */
  get treasuryTotalUsd(): BigNumber {
    if (this.treasuryBalances.value.length === 0) {
      return new BigNumber(NaN)
    }
    return this.treasuryBalances.value.reduce((acc, token) => {
      return acc.plus(token.amountUSD)
    }, new BigNumber(0))
  }

  get currentChainStats(): IChainTokenStat[] {
    const { chainId } = this.rootStore.web3Connection
    const stats = this.stats.value.find((stat) => stat.chainId === chainId)
    return stats ? stats.stats : []
  }

  get bestLendAprs(): { chainId: ChainId; tokens: IChainTokenStat[] }[] {
    return this.stats.value
      .filter((chain) => chain.stats.length > 0) // May happen that a new chain is being added and has no stats yet
      .map((chainStats) => {
        const { chainId, stats } = chainStats
        const orderedCoins = stats.sort((a, b) => +b.supplyApr - +a.supplyApr)
        const { stables, others } = orderedCoins.reduce(
          (acc, coin) => {
            if (+coin.supplyApr >= 0.01) {
              const group = chainConfigs[chainId].stableCoins.includes(coin.tokenName)
                ? acc.stables
                : acc.others
              group.push(coin)
            }
            return acc
          },
          {
            stables: [],
            others: [],
          } as { stables: IChainTokenStat[]; others: IChainTokenStat[] }
        )
        const best = stables.slice(0, 3)
        return { chainId, tokens: best.concat(others.slice(0, 3 - best.length)) }
      })
      .filter((stat) => stat.tokens.length > 0)
      .sort((a, b) => +b.tokens[0].supplyApr - +a.tokens[0].supplyApr)
  }

  get error() {
    return this.stats.error || this.latestProtocolEvents.error || this.selectedUserEvents.error
  }

  /**
   * Refresh stats (total borrow, lent, aprs, etc) for the current chain onchain (not API)
   */
  refreshForCurrentChain() {
    const { chainId } = this.rootStore.web3Connection
    if (!chainId) {
      return
    }
    return this.stats.fetch(chainId, { onChain: true })
  }

  /**
   * Helper to set values through mobx actions.
   */
  set(prop: keyof IStatsStoreProps, value: any) {
    ;(this[prop] as any) = value
  }

  /**
   * Helper to assign multiple props values through a mobx action.
   */
  assign(props: Partial<IStatsStoreProps>) {
    Object.assign(this, props)
  }

  /**
   * Reset error state to null for any model that might have an error.
   * stakingStore.error computed value will be null as a result.
   */
  clearError() {
    this.latestProtocolEvents.error = null
    this.stats.error = null
    this.assetStatsData.error = null
    this.tvlData.error = null
    this.selectedUserEvents.error = null
  }

  constructor(rootStore: RootStore) {
    this.rootStore = rootStore
    mobx.makeAutoObservable(
      this,
      { rootStore: false, treasuryBalances: false, stats: false },
      { autoBind: true, deep: false }
    )
    this.refreshLatestActivityDebounced = debounce(
      () => {
        const { chainId } = this.rootStore.web3Connection
        this.latestProtocolEvents.reset()
        this.latestProtocolEvents.fetch(chainId)
      },
      // fetch events every 15 minutes
      15 * 60 * 1000
    )
  }
}
